// Run E2 J5 (D04, D20): the ONE EN/ES string table for the Project Explore
// student lesson screens (lesson shell, steps, Scene Reader, vocab cards,
// formative + quiz players, practice card, done screens) and the teacher
// materials viewer (tabs, panel headings, content_json section headings).
//
// The language comes from the assignment / lesson (pe:<ch>:<tier>:<lang>), or
// from the Scene Reader / player EN|ES toggle. Components pick a table with
// `peT(lang)`; screens without a lesson language use `useT(PE_T)` from
// stores/uiLang (the shared UI language store from J2).
//
// Spanish: short, warm, natural US-classroom Spanish for K-5 (T1 children are
// 5 or 6). Teachers are "tu maestro o maestra" (no gender guess).

import type { UiLang } from "../stores/uiLang";

type StepKey = "vocab" | "video" | "reader" | "formative" | "activity" | "summative";
type ItemKind =
  | "mc"
  | "audio_match"
  | "audio"
  | "drag_drop"
  | "sentence_builder"
  | "open_response";

export interface PeLessonStrings {
  // ---- shell (LessonSequence, StudentLessonPage, error + missing screens)
  loadingLesson: string;
  steps: Record<StepKey, { label: string; blurb: string }>;
  allStepsDone: string;
  backToDashboard: string;
  answerEveryItem: string;
  saving: string;
  markComplete: string;
  backToCurrent: string;
  continueLabel: string;
  continueBlurb: string;
  stepPanelHint: (markLabel: string) => string;
  lessonCrashTitle: string;
  lessonCrashBody: string;
  refresh: string;
  missingTitle: Record<"reader" | "lesson" | "formative" | "quiz", string>;
  /** "<lead> chapter_id, tier, <and> lang." (the codes render as <code>). */
  missingLead: string;
  missingAnd: string;
  // ---- shared labels
  chapter: string;
  tier: (t: string) => string;
  langName: Record<UiLang, string>;
  displayLanguage: string;
  assigned: string;
  // ---- vocab step
  loadingWords: string;
  vocabTitle: string;
  noWords: string;
  wordsToKnow: string;
  wordsBlurb: string;
  // ---- episode step
  loadingEpisode: string;
  watchTitle: string;
  watchBlurb: string;
  episodeMissing: string;
  episodeFrameTitle: string;
  // ---- activity step
  loadingActivity: string;
  activityTitle: string;
  noActivity: string;
  yourTurn: string;
  activityHint: (markLabel: string) => string;
  // ---- scene reader
  sceneReader: string;
  scenesCount: (n: number) => string;
  playStory: string;
  play: string;
  pause: string;
  playScene: (n: number) => string;
  pauseScene: (n: number) => string;
  sceneAlt: (n: number) => string;
  loadScenesFailed: string;
  // ---- formative / quiz player
  formativeTitle: string;
  quizTitle: string;
  practiceTitle: string;
  /** Built-in practice items (used when the DB practice items do not load). */
  practice: {
    mcPrompt: string;
    mcChoiceA: string;
    mcChoiceB: string;
    audioPrompt: string;
    sentencePrompt: string;
    sentenceWords: string[];
    dragPrompt: string;
    dragItems: string[];
    dragTargets: string[];
    openPrompt: string;
  };
  practiceNote: string;
  imReady: string;
  itemOf: (i: number, n: number) => string;
  itemKind: Record<ItemKind, string>;
  previous: string;
  next: string;
  submit: string;
  submitting: string;
  endOfPreview: string;
  teacherPreviewStrong: string;
  teacherPreviewBody: string;
  heldForReview: string;
  resultCorrect: string;
  resultIncorrect: string;
  resultQueued: string;
  resultSaved: string;
  allDone: string;
  scoreLine: (correct: number, auto: number) => string;
  spokenQueued: (n: number) => string;
  writtenQueued: (n: number) => string;
  loadItemsFailed: string;
  submitFailed: string;
  // ---- item renderers (FormativeItem)
  buildSentenceHint: string;
  writeAnswer: string;
  recordingTapStop: string;
  tapMicAgain: string;
  tapMicSay: string;
  stopRecording: string;
  recordAgain: string;
  recordAnswer: string;
  micError: string;
  trayStatements: string;
  trayPeople: string;
  trayItems: string;
  allPlaced: string;
  placing: string;
  cancel: string;
  bucketCategories: string;
  bucketTreasures: string;
  bucketOrder: string;
  hintSort: string;
  hintMatch: string;
  hintOrder: string;
  position: (n: number) => string;
  bucketAria: (label: string, n: number) => string;
  chipAria: (stmt: string, bucket: string) => string;
  announcePlaced: (item: string, target: string) => string;
  announceReturned: (item: string) => string;
  announcePlacing: (item: string) => string;
  tapToHear: string;
  playQuestionAgain: string;
  // ---- teacher materials viewer (SceneReader role="teacher", D20)
  teacherMaterials: string;
  tabs: Record<
    | "guide"
    | "quickref"
    | "vocab_launch"
    | "readaloud"
    | "checklist"
    | "standards"
    | "family"
    | "assessment"
    | "activity",
    string
  >;
  /** Run E3 B2: materials rail on the curriculum chapter page + print. */
  materialsRail: {
    title: string;
    blurb: string;
    tierSwitch: string;
    forTier: (tier: string) => string;
    print: string;
    printHint: string;
    loading: string;
    loadFailed: string;
    noActivity: string;
    activityTitle: string;
    minutes: string;
    printed: (chapter: string, tier: string, lang: string) => string;
  };
  backToLesson: string;
  noDoc: Record<"guide" | "quickref" | "readaloud", string>;
  loadingLaunch: string;
  noLaunch: string;
  loadLaunchFailed: string;
  loadingStandards: string;
  noStandards: string;
  loadStandardsFailed: string;
  where: string;
  sceneRef: (n: number | string) => string;
  noChecklist: string;
  noFamilyLetter: string;
  loadingAssessment: string;
  noAssessment: string;
  loadAssessmentFailed: string;
  formativeItems: string;
  summativeQuiz: string;
  teacherReviewed: string;
  autoScored: string;
  imageChoice: string;
  dragItems: string;
  /** Standards tab: a CCSS standard statement in this language (unknown -> as given). */
  standardText: (en: string) => string;
  /** Standards tab: framework heading. */
  frameworkName: (name: string) => string;
  /** content_json key -> section heading. Unknown keys fall back to humanizeKey(). */
  docKeys: Record<string, string>;
  /** Screen-reader label of the site toast region (layouts). */
  notificationsAria: string;
  /** Spoken name of a picture tile in a tap-to-place item. */
  picture: (n: number) => string;
  // ---- teacher lesson preview page
  loadingPreview: string;
  backToTeacherDashboard: string;
  lessonPreview: string;
  lessonPreviewBlurb: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
/** A button label quoted in a hint, without its trailing arrow. */
const btn = (label: string) => label.replace(/\s*→\s*$/, "");

// Run E2 J5 round 2: Spanish for the fixed set of CCSS ELA standard statements that
// pe_chapters.standards_alignment uses (55 distinct texts, keyed by the English text).
const CCSS_ES: Record<string, string> = {
  "With prompting and support, retell familiar stories including key details": "Con indicaciones y apoyo, recontar cuentos conocidos con los detalles clave",
  "Ask and answer questions about key details in a text": "Hacer y responder preguntas sobre los detalles clave de un texto",
  "Identify characters, settings, and major events in a story": "Identificar los personajes, el escenario y los sucesos principales de un cuento",
  "Refer to details and examples in a text when explaining what it says (upper tiers)": "Referirse a detalles y ejemplos del texto al explicar lo que dice (niveles superiores)",
  "Explore word relationships and nuances in word meanings": "Explorar la relación entre las palabras y los matices de su significado",
  "Participate in collaborative conversations about texts": "Participar en conversaciones colaborativas sobre los textos",
  "Produce/write an opinion piece with reasons (upper tiers)": "Escribir un texto de opinión con razones (niveles superiores)",
  "Produce/write an opinion or explanatory piece with reasons (upper tiers)": "Escribir un texto de opinión o explicativo con razones (niveles superiores)",
  "Describe how characters respond to major events and challenges": "Describir cómo responden los personajes a los sucesos y retos principales",
  "Produce/write an opinion or informational piece (upper tiers)": "Escribir un texto de opinión o informativo (niveles superiores)",
  "Produce/write an explanatory piece with details (upper tiers)": "Escribir un texto explicativo con detalles (niveles superiores)",
  "Produce/write an opinion or informational piece with reasons (upper tiers)": "Escribir un texto de opinión o informativo con razones (niveles superiores)",
  "Produce/write an informative or explanatory piece (upper tiers)": "Escribir un texto informativo o explicativo (niveles superiores)",
  "Describe the connection between two events in a story (cause and effect)": "Describir la relación entre dos sucesos de un cuento (causa y efecto)",
  "Refer to details and examples in a text when explaining (upper tiers)": "Referirse a detalles y ejemplos del texto al explicar (niveles superiores)",
  "Produce/write an explanation with reasons (upper tiers)": "Escribir una explicación con razones (niveles superiores)",
  "Refer to details and examples in a text when explaining what it says": "Referirse a detalles y ejemplos del texto al explicar lo que dice",
  "Produce/write an informational or opinion piece with reasons (upper tiers)": "Escribir un texto informativo o de opinión con razones (niveles superiores)",
  "Use context clues to determine or clarify word meanings": "Usar pistas del contexto para determinar o aclarar el significado de las palabras",
  "Produce/write an informative or opinion piece with reasons (upper tiers)": "Escribir un texto informativo o de opinión con razones (niveles superiores)",
  "Determine the meaning of words and phrases in a text": "Determinar el significado de palabras y frases de un texto",
  "Produce/write an explanatory or opinion piece with reasons (upper tiers)": "Escribir un texto explicativo o de opinión con razones (niveles superiores)",
  "Produce/write opinion or informational pieces with reasons (upper tiers)": "Escribir textos de opinión o informativos con razones (niveles superiores)",
  "Produce/write an explanation with supporting details (upper tiers)": "Escribir una explicación con detalles de apoyo (niveles superiores)",
  "Explain how the author uses text structure (flashback) to convey meaning": "Explicar cómo el autor usa la estructura del texto (una escena del pasado) para comunicar el significado",
  "Determine or clarify meaning of unknown words in context": "Determinar o aclarar el significado de palabras desconocidas según el contexto",
  "Determine the meaning of words and phrases, including figurative language": "Determinar el significado de palabras y frases, incluido el lenguaje figurado",
  "Describe how characters respond or change as the plot moves toward a resolution": "Describir cómo responden o cambian los personajes a medida que la trama avanza hacia la resolución",
  "Determine the meaning of words and phrases in a text; word relationships and nuances": "Determinar el significado de palabras y frases de un texto; relación entre palabras y matices",
  "Write opinion/explanatory pieces with reasons and evidence (upper tiers)": "Escribir textos de opinión o explicativos con razones y evidencia (niveles superiores)",
  "Describe characters and explain how their actions contribute to the story": "Describir a los personajes y explicar cómo sus acciones contribuyen al cuento",
  "Describe a character's traits and motivations, drawing on details (upper tiers)": "Describir los rasgos y las motivaciones de un personaje, apoyándose en detalles (niveles superiores)",
  "Determine the meaning of words and phrases in a text (domain-specific)": "Determinar el significado de palabras y frases de un texto (vocabulario de la materia)",
  "Explain events, procedures, ideas, or concepts in informational text (process structure)": "Explicar sucesos, procedimientos, ideas o conceptos de un texto informativo (estructura de proceso)",
  "Describe the relationship between events using language such as *first, then, next, finally*": "Describir la relación entre sucesos con palabras como *primero, luego, después, por último*",
  "Produce/write an explanatory piece with reasons (upper tiers)": "Escribir un texto explicativo con razones (niveles superiores)",
  "Use words and phrases acquired through conversations, reading, and being read to": "Usar palabras y frases aprendidas en conversaciones, en la lectura y al escuchar leer",
  "Use information gained from illustrations and text in understanding the text": "Usar la información de las ilustraciones y del texto para comprender el texto",
  "Ask and answer questions to demonstrate understanding, referring to the text": "Hacer y responder preguntas para demostrar comprensión, refiriéndose al texto",
  "Describe characters' traits, motivations, or feelings": "Describir los rasgos, las motivaciones o los sentimientos de los personajes",
  "Determine the meaning of words and phrases as they are used in a text": "Determinar el significado de palabras y frases según se usan en un texto",
  "Write opinion/informative pieces with reasons or explanation (upper tiers)": "Escribir textos de opinión o informativos con razones o explicaciones (niveles superiores)",
  "Describe characters and explain how their actions contribute to the sequence of events": "Describir a los personajes y explicar cómo sus acciones contribuyen a la secuencia de sucesos",
  "Describe characters and explain how their actions contribute to the plot": "Describir a los personajes y explicar cómo sus acciones contribuyen a la trama",
  "Determine the theme or central message and explain how it is conveyed": "Determinar el tema o mensaje central y explicar cómo se transmite",
  "Determine cause-and-effect relationships in narrative text": "Determinar relaciones de causa y efecto en un texto narrativo",
  "Produce/write an informative piece using details and domain-specific vocabulary (upper tiers)": "Escribir un texto informativo con detalles y vocabulario de la materia (niveles superiores)",
  "Retell familiar stories including key details": "Recontar cuentos conocidos con los detalles clave",
  "Determine the meaning of words and phrases as used in a text, including figurative language": "Determinar el significado de palabras y frases según se usan en un texto, incluido el lenguaje figurado",
  "Write an opinion or explanatory piece with reasons (upper tiers)": "Escribir un texto de opinión o explicativo con razones (niveles superiores)",
  "Refer to details and examples in a text when explaining what the text says (upper tiers)": "Referirse a detalles y ejemplos del texto al explicar lo que dice el texto (niveles superiores)",
  "Produce/write an explanatory or opinion piece with evidence (upper tiers)": "Escribir un texto explicativo o de opinión con evidencia (niveles superiores)",
  "With prompting, retell familiar stories including key details": "Con indicaciones, recontar cuentos conocidos con los detalles clave",
  "Determine the meaning of words as they are used in a text": "Determinar el significado de las palabras según se usan en un texto",
  "Participate in collaborative conversations about grade-level topics": "Participar en conversaciones colaborativas sobre temas de su grado",
};

const EN: PeLessonStrings = {
  loadingLesson: "Loading your lesson…",
  steps: {
    vocab: { label: "Vocabulary", blurb: "Warm up with this chapter's key words before the story." },
    video: { label: "Watch the Episode", blurb: "Watch the animated episode for this chapter." },
    reader: { label: "Read Along", blurb: "Read the story with word-by-word narration." },
    formative: {
      label: "Formative Check",
      blurb: "Tap the pictures and say your answers — quick practice as you go.",
    },
    activity: { label: "Activity", blurb: "Complete the chapter activity." },
    summative: { label: "Chapter Quiz", blurb: "Show what you learned." },
  },
  allStepsDone: "🎉 You finished every step of this chapter. Great work!",
  backToDashboard: "← Back to Dashboard",
  answerEveryItem: "Answer every item to continue →",
  saving: "Saving…",
  markComplete: "Mark complete & continue →",
  backToCurrent: "Back to current step →",
  continueLabel: "Continue",
  continueBlurb: "Mark this step complete when you're ready.",
  stepPanelHint: (m) => `When you've finished this step, tap “${btn(m)}”.`,
  lessonCrashTitle: "This lesson couldn’t load",
  lessonCrashBody:
    "Something went wrong rendering this step. Please refresh the page, or go back and try again. The error has been logged for the team.",
  refresh: "Refresh",
  missingTitle: {
    reader: "Scene Reader",
    lesson: "Lesson",
    formative: "Formative Check",
    quiz: "Chapter Quiz",
  },
  missingLead: "No chapter context in this launch. Open this screen from an assignment link that includes",
  missingAnd: "and",
  chapter: "Chapter",
  tier: (t) => `Tier ${t.replace(/^T/i, "")}`,
  langName: { en: "English", es: "Español" },
  displayLanguage: "Display language",
  assigned: "assigned",
  loadingWords: "Loading words…",
  vocabTitle: "Vocabulary",
  noWords: "No words for this chapter yet.",
  wordsToKnow: "Words to Know",
  wordsBlurb: "Look at these words before the story.",
  loadingEpisode: "Loading episode…",
  watchTitle: "Watch the Episode",
  watchBlurb: "Watch the story, then continue.",
  episodeMissing: "The episode for this chapter isn’t available yet.",
  episodeFrameTitle: "Episode",
  loadingActivity: "Loading activity…",
  activityTitle: "Activity",
  noActivity: "No activity for this chapter yet.",
  yourTurn: "Your Turn",
  activityHint: (m) => `Your teacher will guide this activity. When you’ve finished, tap “${btn(m)}”.`,
  sceneReader: "Scene Reader",
  scenesCount: (n) => `${n} ${plural(n, "scene", "scenes")}`,
  playStory: "▶ Play story",
  play: "▶ Play",
  pause: "❚❚ Pause",
  playScene: (n) => `Play scene ${n}`,
  pauseScene: (n) => `Pause scene ${n}`,
  sceneAlt: (n) => `Scene ${n}`,
  loadScenesFailed: "The story could not load. Please refresh the page.",
  formativeTitle: "Formative Check",
  quizTitle: "Chapter Quiz",
  practiceTitle: "Let's try one together!",
  practice: {
    mcPrompt: "Let's practice: which one is the cat?",
    mcChoiceA: "🐱 Cat",
    mcChoiceB: "🚗 Car",
    audioPrompt: "Let's practice: tap the mic and say your favorite color.",
    sentencePrompt: "Let's practice: tap the words in order.",
    sentenceWords: ["I", "like", "to", "play"],
    dragPrompt: "Let's practice: put each one where it goes.",
    dragItems: ["Sun", "Moon"],
    dragTargets: ["Day", "Night"],
    openPrompt: "Let's practice: type one thing you like.",
  },
  practiceNote: "This one is just for practice — it won't be graded.",
  imReady: "I'm ready! →",
  itemOf: (i, n) => `Item ${i} of ${n}`,
  itemKind: {
    mc: "Choose one",
    audio_match: "Say it",
    audio: "Say it",
    drag_drop: "Put in place",
    sentence_builder: "Build a sentence",
    open_response: "Write",
  },
  previous: "← Previous",
  next: "Next →",
  submit: "Submit",
  submitting: "Submitting…",
  endOfPreview: "End of preview",
  teacherPreviewStrong: "Teacher preview.",
  teacherPreviewBody: "You can review every item, but answers are not submitted or scored.",
  heldForReview: "Your teacher will check this before it gets a grade.",
  resultCorrect: "Correct!",
  resultIncorrect: "Not this time.",
  resultQueued: "Sent to your teacher.",
  resultSaved: "Saved.",
  allDone: "All done!",
  scoreLine: (c, a) => `${c} auto-scored correct of ${a} auto-graded items.`,
  spokenQueued: (n) =>
    `${n} spoken ${plural(n, "answer", "answers")} sent to your teacher for review.`,
  writtenQueued: (n) =>
    `${n} written ${plural(n, "answer", "answers")} sent to your teacher for review.`,
  loadItemsFailed: "The questions could not load. Please refresh the page.",
  submitFailed: "Your answer was not sent. Please try again.",
  buildSentenceHint: "Tap words below to build your sentence…",
  writeAnswer: "Write your answer…",
  recordingTapStop: "● Recording… tap to stop",
  tapMicAgain: "Tap the mic to record again",
  tapMicSay: "Tap the mic and say your answer",
  stopRecording: "Stop recording",
  recordAgain: "Record again",
  recordAnswer: "Record your answer",
  micError: "The microphone is not working. Ask your teacher for help.",
  trayStatements: "Statements",
  trayPeople: "Pieces",
  trayItems: "Items",
  allPlaced: "All items placed.",
  placing: "Placing:",
  cancel: "Cancel",
  bucketCategories: "Categories",
  bucketTreasures: "Matches",
  bucketOrder: "Order",
  hintSort: "Tap a statement above to select it, then tap a category to sort it.",
  hintMatch: "Tap a piece above to select it, then tap where it belongs.",
  hintOrder: "Tap a picture above, then tap a number to place it.",
  position: (n) => `Position ${n}`,
  bucketAria: (l, n) => `${l}, ${n} ${plural(n, "item", "items")}`,
  chipAria: (s, b) => `${s}, in ${b}, activate to remove`,
  announcePlaced: (i, t) => `${i} placed in ${t}`,
  announceReturned: (i) => `${i} returned`,
  announcePlacing: (i) => `Placing ${i}`,
  tapToHear: "Tap to hear the question",
  playQuestionAgain: "Play the question again",
  teacherMaterials: "Teacher Materials",
  tabs: {
    guide: "Teacher Guide",
    quickref: "Quick Reference",
    vocab_launch: "Vocab Launch",
    readaloud: "Read-Aloud",
    checklist: "Observation Checklist",
    standards: "Standards",
    family: "Family Letter",
    assessment: "Assessment",
    activity: "Activity",
  },
  materialsRail: {
    title: "Teacher materials",
    blurb: "Open any material now. You do not need to assign the chapter first.",
    tierSwitch: "Tier",
    forTier: (tier) => `For ${tier}`,
    print: "Print this page",
    printHint: "Prints on letter paper with no menus.",
    loading: "Loading materials…",
    loadFailed: "The materials could not load.",
    noActivity: "No activity for this chapter yet.",
    activityTitle: "Extension activity",
    minutes: "min",
    printed: (chapter, tier, lang) => `Project Explore, ${chapter}, ${tier}, ${lang}`,
  },
  backToLesson: "← Back to lesson",
  noDoc: {
    guide: "No teacher guide for this chapter yet.",
    quickref: "No quick reference for this chapter yet.",
    readaloud: "No read-aloud script for this chapter yet.",
  },
  loadingLaunch: "Loading launch cards…",
  noLaunch: "No launch cards for this chapter yet.",
  loadLaunchFailed: "The launch cards could not load.",
  loadingStandards: "Loading standards…",
  noStandards: "No standards for this chapter yet.",
  loadStandardsFailed: "The standards could not load.",
  where: "Where:",
  sceneRef: (n) => `scene ${n}`,
  noChecklist: "No observation checklist for this chapter/language.",
  noFamilyLetter: "No family letter for this chapter/language.",
  loadingAssessment: "Loading assessment items…",
  noAssessment: "No assessment items for this chapter/tier/language.",
  loadAssessmentFailed: "The assessment items could not load.",
  formativeItems: "Formative Items",
  summativeQuiz: "Summative Quiz",
  teacherReviewed: "teacher-reviewed",
  autoScored: "auto-scored",
  imageChoice: "🖼 (image choice)",
  dragItems: "Pieces to place:",
  standardText: (en) => en,
  frameworkName: (n) => n,
  // EN keeps the old humanized headings; only the ones that read wrong are set.
  docKeys: {
    ela: "ELA",
    fl_throughline: "FL Throughline",
    t20: "20 min",
    t30: "30 min",
    t45: "45 min",
    T1: "Tier 1",
    T2: "Tier 2",
    T3: "Tier 3",
    T4: "Tier 4",
    body_md: "Letter",
    print_path: "Print path",
    read_the_big_book: "Read the big book",
    paper_practice: "Paper practice",
    turn_in: "Turn-in",
    id: "ID",
  },
  notificationsAria: "Notifications Alt+T",
  picture: (n) => `Picture ${n}`,
  loadingPreview: "Loading lesson preview…",
  backToTeacherDashboard: "← Back to dashboard",
  lessonPreview: "Lesson Preview",
  lessonPreviewBlurb: "Preview of the assigned Project Explore lesson, with your teacher materials.",
};

const ES: PeLessonStrings = {
  loadingLesson: "Cargando tu lección…",
  steps: {
    vocab: { label: "Vocabulario", blurb: "Conoce las palabras clave del capítulo antes del cuento." },
    video: { label: "Mira el episodio", blurb: "Mira el episodio animado de este capítulo." },
    reader: { label: "Lee y escucha", blurb: "Lee el cuento mientras lo escuchas palabra por palabra." },
    formative: {
      label: "Repaso rápido",
      blurb: "Toca los dibujos y di tus respuestas. Es una práctica corta.",
    },
    activity: { label: "Actividad", blurb: "Haz la actividad del capítulo." },
    summative: { label: "Prueba del capítulo", blurb: "Muestra lo que aprendiste." },
  },
  allStepsDone: "🎉 ¡Terminaste todos los pasos de este capítulo! ¡Buen trabajo!",
  backToDashboard: "← Volver a mi panel",
  answerEveryItem: "Contesta todas las preguntas para seguir →",
  saving: "Guardando…",
  markComplete: "Terminé, ¡a seguir! →",
  backToCurrent: "Volver al paso de ahora →",
  continueLabel: "Seguir",
  continueBlurb: "Cuando estés listo o lista, marca este paso como terminado.",
  stepPanelHint: (m) => `Cuando termines este paso, toca «${btn(m)}».`,
  lessonCrashTitle: "No se pudo abrir esta lección",
  lessonCrashBody:
    "Algo salió mal en este paso. Vuelve a cargar la página o regresa e inténtalo otra vez. Ya avisamos al equipo.",
  refresh: "Volver a cargar",
  missingTitle: {
    reader: "Lee y escucha",
    lesson: "Lección",
    formative: "Repaso rápido",
    quiz: "Prueba del capítulo",
  },
  missingLead:
    "Falta la información del capítulo en este enlace. Abre esta pantalla desde el enlace de una tarea que incluya",
  missingAnd: "y",
  chapter: "Capítulo",
  tier: (t) => `Nivel ${t.replace(/^T/i, "")}`,
  langName: { en: "English", es: "Español" },
  displayLanguage: "Idioma",
  assigned: "asignado",
  loadingWords: "Cargando palabras…",
  vocabTitle: "Vocabulario",
  noWords: "Todavía no hay palabras para este capítulo.",
  wordsToKnow: "Palabras para aprender",
  wordsBlurb: "Mira estas palabras antes del cuento.",
  loadingEpisode: "Cargando el episodio…",
  watchTitle: "Mira el episodio",
  watchBlurb: "Mira el cuento y luego sigue.",
  episodeMissing: "El episodio de este capítulo todavía no está listo.",
  episodeFrameTitle: "Episodio",
  loadingActivity: "Cargando la actividad…",
  activityTitle: "Actividad",
  noActivity: "Todavía no hay actividad para este capítulo.",
  yourTurn: "Te toca a ti",
  activityHint: (m) =>
    `Tu maestro o maestra te va a guiar en esta actividad. Cuando termines, toca «${btn(m)}».`,
  sceneReader: "Lee y escucha",
  scenesCount: (n) => `${n} ${plural(n, "escena", "escenas")}`,
  playStory: "▶ Escuchar el cuento",
  play: "▶ Escuchar",
  pause: "❚❚ Pausa",
  playScene: (n) => `Escuchar la escena ${n}`,
  pauseScene: (n) => `Pausar la escena ${n}`,
  sceneAlt: (n) => `Escena ${n}`,
  loadScenesFailed: "No se pudo abrir el cuento. Vuelve a cargar la página.",
  formativeTitle: "Repaso rápido",
  quizTitle: "Prueba del capítulo",
  practiceTitle: "¡Hagamos una juntos!",
  practice: {
    mcPrompt: "Practiquemos: ¿Cuál es el gato?",
    mcChoiceA: "🐱 Gato",
    mcChoiceB: "🚗 Carro",
    audioPrompt: "Practiquemos: toca el micrófono y di tu color favorito.",
    sentencePrompt: "Practiquemos: toca las palabras en orden.",
    sentenceWords: ["Me", "gusta", "jugar"],
    dragPrompt: "Practiquemos: pon cada uno en su lugar.",
    dragItems: ["Sol", "Luna"],
    dragTargets: ["Día", "Noche"],
    openPrompt: "Practiquemos: escribe algo que te gusta.",
  },
  practiceNote: "Esta es solo para practicar. No cuenta para tu nota.",
  imReady: "¡Ya estoy! →",
  itemOf: (i, n) => `Pregunta ${i} de ${n}`,
  itemKind: {
    mc: "Elige una",
    audio_match: "Dilo",
    audio: "Dilo",
    drag_drop: "Pon en su lugar",
    sentence_builder: "Arma la oración",
    open_response: "Escribe",
  },
  previous: "← Anterior",
  next: "Siguiente →",
  submit: "Enviar",
  submitting: "Enviando…",
  endOfPreview: "Fin de la vista previa",
  teacherPreviewStrong: "Vista previa para docentes.",
  teacherPreviewBody: "Puedes ver cada pregunta, pero las respuestas no se envían ni se califican.",
  heldForReview: "Tu maestro o maestra la va a revisar antes de ponerle nota.",
  resultCorrect: "¡Correcto!",
  resultIncorrect: "Esta vez no.",
  resultQueued: "Se la enviamos a tu maestro o maestra.",
  resultSaved: "Guardado.",
  allDone: "¡Terminaste!",
  scoreLine: (c, a) => `Acertaste ${c} de ${a} ${plural(a, "pregunta", "preguntas")}.`,
  spokenQueued: (n) =>
    `Tu maestro o maestra va a escuchar ${n} ${plural(n, "respuesta hablada", "respuestas habladas")}.`,
  writtenQueued: (n) =>
    `Tu maestro o maestra va a leer ${n} ${plural(n, "respuesta escrita", "respuestas escritas")}.`,
  loadItemsFailed: "No se pudieron abrir las preguntas. Vuelve a cargar la página.",
  submitFailed: "Tu respuesta no se envió. Inténtalo otra vez.",
  buildSentenceHint: "Toca las palabras de abajo para armar tu oración…",
  writeAnswer: "Escribe tu respuesta…",
  recordingTapStop: "● Grabando… toca para parar",
  tapMicAgain: "Toca el micrófono para grabar otra vez",
  tapMicSay: "Toca el micrófono y di tu respuesta",
  stopRecording: "Parar la grabación",
  recordAgain: "Grabar otra vez",
  recordAnswer: "Graba tu respuesta",
  micError: "El micrófono no funciona. Pide ayuda a tu maestro o maestra.",
  trayStatements: "Frases",
  trayPeople: "Piezas",
  trayItems: "Piezas",
  allPlaced: "¡Ya pusiste todo!",
  placing: "Vas a poner:",
  cancel: "Cancelar",
  bucketCategories: "Grupos",
  bucketTreasures: "Parejas",
  bucketOrder: "Orden",
  hintSort: "Toca una frase de arriba y luego toca el grupo donde va.",
  hintMatch: "Toca una pieza de arriba y luego toca donde va.",
  hintOrder: "Toca un dibujo de arriba y luego toca un número para ponerlo.",
  position: (n) => `Lugar ${n}`,
  bucketAria: (l, n) => `${l}, ${n} ${plural(n, "pieza", "piezas")}`,
  chipAria: (s, b) => `${s}, en ${b}, toca para quitar`,
  announcePlaced: (i, t) => `${i} va en ${t}`,
  announceReturned: (i) => `${i} regresó`,
  announcePlacing: (i) => `Vas a poner ${i}`,
  tapToHear: "Toca para escuchar la pregunta",
  playQuestionAgain: "Escuchar la pregunta otra vez",
  teacherMaterials: "Materiales para docentes",
  tabs: {
    guide: "Guía del docente",
    quickref: "Guía rápida",
    vocab_launch: "Vocabulario de inicio",
    readaloud: "Lectura en voz alta",
    checklist: "Lista de observación",
    standards: "Estándares",
    family: "Carta a las familias",
    assessment: "Evaluación",
    activity: "Actividad",
  },
  materialsRail: {
    title: "Materiales para docentes",
    blurb: "Abre cualquier material ahora. No hace falta asignar el capítulo primero.",
    tierSwitch: "Nivel",
    forTier: (tier) => `Para ${tier}`,
    print: "Imprimir esta página",
    printHint: "Se imprime en papel carta, sin menús.",
    loading: "Cargando materiales…",
    loadFailed: "No se pudieron cargar los materiales.",
    noActivity: "Este capítulo aún no tiene actividad.",
    activityTitle: "Actividad de extensión",
    minutes: "min",
    printed: (chapter, tier, lang) => `Project Explore, ${chapter}, ${tier}, ${lang}`,
  },
  backToLesson: "← Volver a la lección",
  noDoc: {
    guide: "Todavía no hay guía del docente para este capítulo.",
    quickref: "Todavía no hay guía rápida para este capítulo.",
    readaloud: "Todavía no hay guion de lectura en voz alta para este capítulo.",
  },
  loadingLaunch: "Cargando las tarjetas de inicio…",
  noLaunch: "Todavía no hay tarjetas de inicio para este capítulo.",
  loadLaunchFailed: "No se pudieron cargar las tarjetas de inicio.",
  loadingStandards: "Cargando los estándares…",
  noStandards: "Todavía no hay estándares para este capítulo.",
  loadStandardsFailed: "No se pudieron cargar los estándares.",
  where: "Dónde:",
  sceneRef: (n) => `escena ${n}`,
  noChecklist: "No hay lista de observación para este capítulo en este idioma.",
  noFamilyLetter: "No hay carta a las familias para este capítulo en este idioma.",
  loadingAssessment: "Cargando las preguntas de evaluación…",
  noAssessment: "No hay preguntas de evaluación para este capítulo, nivel e idioma.",
  loadAssessmentFailed: "No se pudieron cargar las preguntas de evaluación.",
  formativeItems: "Preguntas formativas",
  summativeQuiz: "Prueba sumativa",
  teacherReviewed: "la revisa el docente",
  autoScored: "calificación automática",
  imageChoice: "🖼 (opción con imagen)",
  dragItems: "Piezas para colocar:",
  standardText: (en) => CCSS_ES[en] ?? en,
  frameworkName: (n) => (n === "CCSS ELA/Literacy" ? "CCSS de Lengua y Literatura (ELA)" : n),
  docKeys: {
    activity_plan: "Plan de actividad",
    apply_word: "Palabra para aplicar",
    assessment: "Evaluación",
    assets_used: "Recursos usados",
    behavior_text: "Conducta",
    behaviors: "Conductas",
    big_idea: "Idea principal",
    bilingual_note: "Nota bilingüe",
    body_md: "Carta",
    print_path: "Camino impreso",
    read_the_big_book: "Lee el libro grande",
    paper_practice: "Práctica en papel",
    turn_in: "Entrega",
    chapter: "Capítulo",
    chapter_id: "Capítulo (id)",
    comprehension_prompts: "Preguntas de comprensión",
    connect: "Conectar",
    definition: "Definición",
    differentiation: "Diferenciación",
    ela: "Lengua y literatura (ELA)",
    episode: "Episodio",
    essential_question: "Pregunta esencial",
    facilitation_tips: "Consejos para guiar",
    family_connection: "Conexión con la familia",
    family_letter: "Carta a las familias",
    fl_throughline: "Hilo de educación financiera",
    formative_items: "Preguntas formativas",
    genre: "Género",
    grade_band: "Grados",
    header: "Encabezado",
    how_to_read: "Cómo leer",
    id: "ID",
    intro: "Introducción",
    language: "Idioma",
    launch_words: "Palabras de inicio",
    lesson_arc: "Secuencia de la lección",
    lesson_time: "Duración de la lección",
    literacy_objective: "Objetivo de lectoescritura",
    look_fors: "Qué observar",
    minutes: "Minutos",
    note: "Nota",
    observation_checklist: "Lista de observación",
    output: "Producto",
    pacing: "Ritmo",
    phase: "Fase",
    phases: "Fases",
    practice: "Practicar",
    purpose: "Propósito",
    reading_tiers: "Niveles de lectura",
    research_anchor: "Base en la investigación",
    scaffolding_prompts: "Preguntas de apoyo",
    script: "Guion",
    season: "Temporada",
    show: "Mostrar",
    standards: "Estándares",
    story_context: "Contexto del cuento",
    T1: "Nivel 1",
    T2: "Nivel 2",
    T3: "Nivel 3",
    T4: "Nivel 4",
    t20: "20 min",
    t30: "30 min",
    t45: "45 min",
    teacher_does: "Qué hace el docente",
    teaching_note: "Nota para enseñar",
    term: "Término",
    tier: "Nivel",
    tier_outputs: "Productos por nivel",
    total_time: "Tiempo total",
    vocabulary: "Vocabulario",
    watch: "Observar",
    word: "Palabra",
  },
  notificationsAria: "Notificaciones Alt+T",
  picture: (n) => `Dibujo ${n}`,
  loadingPreview: "Cargando la vista previa de la lección…",
  backToTeacherDashboard: "← Volver al panel",
  lessonPreview: "Vista previa de la lección",
  lessonPreviewBlurb: "Vista previa de la lección de Project Explore asignada, con sus materiales para docentes.",
};

export const PE_T: Record<UiLang, PeLessonStrings> = { en: EN, es: ES };

/** String table for a lesson / content language ("en" | "es"; anything else -> EN). */
export function peT(lang: string | null | undefined): PeLessonStrings {
  return lang === "es" ? ES : EN;
}

/** Humanize a content_json key (old behaviour, the fallback for unmapped keys). */
export function humanizeKey(k: string): string {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Section heading for a content_json key in the doc's language. */
export function docKeyLabel(lang: string | null | undefined, key: string): string {
  return peT(lang).docKeys[key] ?? humanizeKey(key);
}
